<script>
  import { scaleLinear, scaleLog } from "d3-scale";
  import { extent } from "d3-array";
  import { format } from "d3-format";
  import Bubble from "./Bubble.svelte";
  import Label from "./Label.svelte";
  import Tooltip from "./Tooltip.svelte";
  import Grid from "./Grid.svelte";
  import Loading from "./Loading.svelte";
  import NoDataAvailable from "./NoDataAvailable.svelte";
  import { displayData } from "./../stores";
  import { colors } from "./../colors";

  export let benchmarkValue;
  export let benchmarkData;
  export let dotRadius;
  export let dotOpacity;
  export let dotColor;
  export let chartTitle;
  export let primaryCountry;
  export let referenceCountries;
  export let benchmarkLoading;
  export let highlightGroup;

  let primaryCountryData = [];

  $: if (primaryCountry) {
    primaryCountryData = benchmarkData.map((inddata) => {
      if (primaryCountry) {
        return inddata.find((d) => d.label === primaryCountry.label);
      }
    });
  }

  let referenceCountryData = [];

  $: if (referenceCountries && typeof benchmarkData !== "undefined") {
    referenceCountryData = benchmarkData.map((inddata) => {
      if (referenceCountries) {
        let refCntrs = referenceCountries.map((d) => d.label);
        return inddata.filter((d) => refCntrs.includes(d.label));
      }
    });
  }

  $: highlightData = typeof benchmarkData !== "undefined"
    ? benchmarkData.flat().filter((d) => {
      if (highlightGroup.group === "Income group") {
        return d.income === highlightGroup.label;
      }
      if (highlightGroup.group === "Region") {
        return d.wbregion === highlightGroup.label;
      }
      if (highlightGroup.group === "Fragile") {
        return d.fragile === highlightGroup.label;
      }
      if (highlightGroup.group === "Lending") {
        return d.lending === highlightGroup.label;
      }
    })
    .map((d) => d.label)
    : [];

  const margin = { top: 40, bottom: 50, left: 50, right: 20 };
  const width = 800;
  let height = 0;
  $: if (typeof benchmarkData !== "undefined") {
    height = benchmarkData.length * 100 + 40;
  }
  const innerHeight = height - margin.top - margin.bottom;
  const innerWidth = width - margin.left - margin.right;

  let showTooltip = false;
  let tooltipContent;
  let tooltipX;
  let tooltipY;

  $: scales =
    typeof benchmarkData !== "undefined"
      ? benchmarkData.map((bmd) => {
          return scaleLinear()
            .domain(extent(bmd, (e) => e.x))
            .range([0, innerWidth]);
        })
      : [];

  const benchmarkMargin = 100;
</script>

{#if benchmarkValue === null}
  <p class="message">Select at least one variable</p>
{/if}

<svg {width} {height} class="chart">
  <g transform={`translate(${margin.left},${margin.top})`}>
    <text x={0} y={-20} font-size={24}>{chartTitle}</text>
    {#if benchmarkValue !== null}
      {#each benchmarkValue as bmIndicator, i}
        <g transform={`translate(0,${benchmarkMargin * i})`}>
          {#if benchmarkLoading[i]}
            <Loading width={innerWidth} height={innerHeight} />
          {:else}
            <text x={0} y={10}>{bmIndicator.label}</text>
            <g transform="translate(0,20)">
              {#if benchmarkData[i].length === 0}
                <NoDataAvailable width={innerWidth} height={40} />
              {/if}

              {#if benchmarkValue.length === benchmarkData.length && benchmarkValue.length === scales.length}
                <Grid
                  gridType="xGrid"
                  innerHeight={40}
                  {innerWidth}
                  scale={scales[i]}
                  ticks={scales[i].ticks()}
                  numberFormat=",~r"
                />

                {#if $displayData}
                  {#each benchmarkData[i] as d}
                    <Bubble
                      cx={scales[i](d.x)}
                      cy={20}
                      fill={highlightData.includes(d.label)
                        ? dotColor
                        : primaryCountry && primaryCountry.label === d.label
                          ? colors.primary 
                          : referenceCountries !== null && referenceCountries.map(d => d.label).includes(d.label)
                            ? colors.reference
                            : "#cccccc"}
                      opacity={dotOpacity}
                      r={dotRadius}
                      ttContent={`<p class="countryname">${d.label}</p>
                                  <p>${benchmarkValue[i].label}: <span class="value">${format(",.1f")(d.x)}</span></p>
                                  <p>Value for the year ${d.year}</p>`}
                      bind:tooltipContent
                      bind:tooltipX
                      bind:tooltipY
                      bind:showTooltip
                    />
                  {/each}
                {/if}

                {#if referenceCountries}
                  {#each referenceCountryData[i] as refCntr}
                    <circle
                      cx={scales[i](refCntr.x)}
                      cy={20}
                      r={dotRadius + 5}
                      fill-opacity={0}
                      stroke-width={4}
                      stroke={"#ffffff"}
                    />
                    <circle
                      cx={scales[i](refCntr.x)}
                      cy={20}
                      r={dotRadius + 5}
                      fill-opacity={0}
                      stroke-width={1.5}
                      stroke={colors.reference}
                    />
                    <Label
                      x={scales[i](refCntr.x)}
                      y={20}
                      fill={colors.reference}
                      text={refCntr.label}
                      font-size={12}
                      textAnchor="middle"
                      lineLabel={false}
                    />
                  {/each}
                {/if}

                {#if primaryCountry && primaryCountryData[i]}
                  <circle
                    cx={scales[i](primaryCountryData[i].x)}
                    cy={20}
                    r={dotRadius + 5}
                    fill-opacity={0}
                    stroke-width={4}
                    stroke={"#ffffff"}
                  />
                  <circle
                    cx={scales[i](primaryCountryData[i].x)}
                    cy={20}
                    r={dotRadius + 5}
                    fill-opacity={0}
                    stroke-width={1.5}
                    stroke={colors.primary}
                  />
                  <Label
                    x={scales[i](primaryCountryData[i].x)}
                    y={20}
                    fill={colors.primary}
                    text={primaryCountry.label}
                    font-size={12}
                    textAnchor="middle"
                    lineLabel={false}
                  />
                {/if}
              {/if}
            </g>
          {/if}
        </g>
      {/each}
    {/if}
  </g>
</svg>
{#if showTooltip}
  <Tooltip {tooltipX} {tooltipY} {tooltipContent} />
{/if}

<style>
  .message {
    font-size: 24px;
  }
</style>
